// Phase 11 - Billing: Payment Gateway, Pricing, Coupons, Checkout, Invoices,
// Refunds, Subscription Plans, Org Billing, Revenue Dashboard.
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Receipt, IndianRupee, Tag, CreditCard, FileText,
  TrendingUp, Users, Package, Trash2, CheckCircle2, XCircle, Download,
  Sparkles, Building2, AlertTriangle, RefreshCw, Loader2,
} from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/StatCard";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { RowActionsMenu } from "@/components/admin/RowActionsMenu";
import { StatusFilterTabs } from "@/components/admin/StatusFilterTabs";
import { useLifecycleActions } from "@/components/admin/useLifecycleActions";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const INR = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ============================================================
// LEARNER — CART + CHECKOUT
// ============================================================

export function LearnerCartPage() {
  const nav = useNavigate();
  const [cart, setCart] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [applied, setApplied] = useState(null);
  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const { data } = await api.get("/learner/cart");
      setCart(data.data || { items: [], subtotal: 0 });
    } catch (err) { toast.error(formatApiError(err)); setCart({ items: [], subtotal: 0 }); }
  };
  const remove = async (id) => {
    try { await api.delete(`/learner/cart/items/${id}`); toast.success("Removed"); load(); setApplied(null); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const apply = async () => {
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.post("/learner/checkout/apply-coupon", { coupon_code: couponCode });
      const r = data.data || {};
      if (r.applied) { setApplied(r); toast.success(`Coupon applied — ${INR(r.discount)} off`); }
      else { setApplied(null); toast.error(r.message || "Invalid coupon"); }
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (!cart) return <DashboardLayout title="Cart"><LoadingBlock /></DashboardLayout>;
  const subtotal = cart.subtotal || 0;
  const discount = applied?.discount || 0;
  const taxable = Math.max(0, subtotal - discount);
  const tax = Math.round(taxable * 0.18 * 100) / 100;
  const total = Math.round((taxable + tax) * 100) / 100;
  return (
    <DashboardLayout title="Shopping Cart" breadcrumbs={["My Learning", "Cart"]}>
      <div className="grid gap-4 lg:grid-cols-[1fr,340px]" data-testid="learner-cart">
        <div className="space-y-3">
          {cart.items.length === 0 ? (
            <EmptyState title="Your cart is empty" description="Browse the course catalog to add items." icon={ShoppingCart} />
          ) : cart.items.map((it) => (
            <div key={it.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-4" data-testid={`cart-item-${it.id}`}>
              <Package className="h-8 w-8 text-[#101876]" />
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-800">{it.course?.course_title || "Course"}</div>
                <div className="text-[11px] text-slate-500">{it.course?.course_code}</div>
              </div>
              <div className="text-sm font-semibold text-slate-800">{INR(it.price)}</div>
              <button type="button" onClick={() => remove(it.id)} data-testid={`cart-remove-${it.id}`} className="text-rose-600 hover:text-rose-700">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3 h-fit sticky top-4" data-testid="cart-summary">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Order summary</div>
          <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span>{INR(subtotal)}</span></div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Coupon code</label>
            <div className="mt-1 flex gap-2">
              <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                     placeholder="WELCOME10" data-testid="cart-coupon-input"
                     className="flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs uppercase" />
              <button type="button" onClick={apply} data-testid="cart-coupon-apply"
                      className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold hover:bg-slate-200">Apply</button>
            </div>
          </div>
          {discount > 0 && (
            <div className="flex justify-between text-sm text-green-700"><span>Discount</span><span>-{INR(discount)}</span></div>
          )}
          <div className="flex justify-between text-sm"><span className="text-slate-600">GST (18%)</span><span>{INR(tax)}</span></div>
          <div className="border-t border-slate-200 pt-2 flex justify-between text-base font-bold text-slate-800">
            <span>Total</span><span data-testid="cart-total">{INR(total)}</span>
          </div>
          <button type="button"
                  onClick={() => nav("/app/learner/checkout" + (applied ? `?coupon=${applied.coupon_code}` : ""))}
                  disabled={cart.items.length === 0}
                  data-testid="cart-checkout-btn"
                  className="w-full rounded-lg bg-gradient-to-r from-[#101876] to-[#329112] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            Proceed to Checkout
          </button>
        </div>
      </div>
    </DashboardLayout>
  );
}

export function LearnerCheckoutPage() {
  const nav = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    billing_name: "", billing_email: "", billing_mobile: "", billing_address: "",
    billing_city: "", billing_state: "Delhi", billing_country: "India",
    billing_pincode: "", billing_gst_number: "",
  });
  const [order, setOrder] = useState(null);
  const [processing, setProcessing] = useState(false);
  const couponCode = params.get("coupon") || "";
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const create = async () => {
    setProcessing(true);
    try {
      const { data } = await api.post("/learner/checkout/create-order", { ...form, coupon_code: couponCode });
      setOrder(data.data);
      toast.success("Order created — proceed to payment");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProcessing(false); }
  };
  const testPay = async () => {
    if (!order?.order?.id) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/learner/checkout/test-pay/${order.order.id}`);
      toast.success(`Payment successful! Invoice ${data.data?.invoice_number}`);
      setTimeout(() => nav("/app/learner/my-courses"), 1500);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProcessing(false); }
  };
  const livePay = () => {
    if (!order?.razorpay_order_id) return;
    if (!window.Razorpay) {
      toast.error("Razorpay SDK not loaded — falling back to test pay");
      return testPay();
    }
    const opts = {
      key: order.razorpay_key_id, amount: order.amount_paise, currency: "INR",
      order_id: order.razorpay_order_id, name: "Steward Learning Cloud",
      description: `Order ${order.order.order_number}`,
      prefill: { name: form.billing_name, email: form.billing_email, contact: form.billing_mobile },
      handler: async (res) => {
        try {
          const { data } = await api.post("/learner/checkout/verify-payment", {
            order_id: order.order.id, razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id, razorpay_signature: res.razorpay_signature,
          });
          toast.success(`Payment verified! Invoice ${data.data?.invoice_number}`);
          setTimeout(() => nav("/app/learner/my-courses"), 1500);
        } catch (err) { toast.error(formatApiError(err)); }
      },
    };
    new window.Razorpay(opts).open();
  };
  return (
    <DashboardLayout title="Checkout" breadcrumbs={["Cart", "Checkout"]}>
      <div className="grid gap-4 lg:grid-cols-[1fr,340px]" data-testid="learner-checkout">
        {!order ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-3">
            <div className="text-sm font-bold text-slate-800">Billing details</div>
            {[
              ["billing_name", "Full name"], ["billing_email", "Email"], ["billing_mobile", "Mobile"],
              ["billing_address", "Address"], ["billing_city", "City"], ["billing_state", "State"],
              ["billing_pincode", "Pincode"], ["billing_gst_number", "GST number (optional)"],
            ].map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</label>
                <input type="text" value={form[k]} onChange={(e) => upd(k, e.target.value)}
                       data-testid={`checkout-${k}`}
                       className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" />
              </div>
            ))}
            <button type="button" onClick={create} disabled={processing || !form.billing_name || !form.billing_email}
                    data-testid="checkout-create-order"
                    className="w-full rounded-lg bg-[#101876] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
              {processing ? "Processing…" : "Continue to Payment"}
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border-2 border-[#329112] bg-gradient-to-br from-white to-green-50 p-6 space-y-3" data-testid="checkout-payment-panel">
            <div className="text-sm font-bold text-slate-800">Pay {INR(order.order.total_amount)}</div>
            <div className="text-[11px] text-slate-500">Order: {order.order.order_number}</div>
            {order.test_mode && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                <strong>Test mode active.</strong> Razorpay keys not configured — click &ldquo;Simulate Payment&rdquo; to complete the flow. Configure <code>RAZORPAY_KEY_ID</code> in <code>backend/.env</code> to enable live checkout.
              </div>
            )}
            {order.test_mode ? (
              <button type="button" onClick={testPay} disabled={processing} data-testid="checkout-test-pay"
                      className="w-full rounded-lg bg-gradient-to-r from-[#101876] to-[#329112] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {processing ? "Processing…" : "Simulate Payment (Test Mode)"}
              </button>
            ) : (
              <button type="button" onClick={livePay} data-testid="checkout-live-pay"
                      className="w-full rounded-lg bg-gradient-to-r from-[#101876] to-[#329112] px-4 py-2 text-sm font-semibold text-white">
                Pay via Razorpay
              </button>
            )}
          </div>
        )}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-2 h-fit">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Summary</div>
          {order ? (
            <>
              <div className="flex justify-between text-sm"><span className="text-slate-600">Subtotal</span><span>{INR(order.order.subtotal)}</span></div>
              {order.order.discount_amount > 0 && <div className="flex justify-between text-sm text-green-700"><span>Discount</span><span>-{INR(order.order.discount_amount)}</span></div>}
              <div className="flex justify-between text-sm"><span className="text-slate-600">GST</span><span>{INR(order.order.tax_amount)}</span></div>
              <div className="border-t pt-2 flex justify-between text-base font-bold"><span>Total</span><span>{INR(order.order.total_amount)}</span></div>
            </>
          ) : (
            <div className="text-xs text-slate-500">Fill your details to see summary</div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export function LearnerOrdersPage() {
  const [rows, setRows] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get("/learner/orders");
      setRows(data.data?.orders || []);
    } catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  if (rows === null) return <DashboardLayout title="My Orders"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="My Orders" breadcrumbs={["My Learning", "Orders"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="learner-orders">
        {rows.length === 0 ? (
          <EmptyState title="No orders yet" description="Your purchase history will appear here." icon={Receipt} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3 text-left">Order</th><th className="p-3 text-left">Date</th><th className="p-3 text-right">Amount</th><th className="p-3 text-center">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => (
                <tr key={o.id} data-testid={`order-${o.id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_number}</td>
                  <td className="p-3 text-[11px] text-slate-500">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="p-3 text-right font-semibold">{INR(o.total_amount)}</td>
                  <td className="p-3 text-center">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      o.payment_status === "paid" ? "bg-green-100 text-green-800" :
                      o.payment_status === "failed" ? "bg-rose-100 text-rose-700" :
                      o.payment_status === "refunded" ? "bg-slate-100 text-slate-600" :
                      o.payment_status === "partially_refunded" ? "bg-slate-100 text-slate-600" :
                      "bg-amber-100 text-amber-700"
                    }`}>{o.payment_status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

function LearnerRefundDialog_UNUSED() { return null; }

export function LearnerInvoicesPage() {
  const [rows, setRows] = useState(null);
  const [receipts, setReceipts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/learner/invoices");
        setRows(data.data?.invoices || []);
        setReceipts(data.data?.enrollment_receipts || []);
      }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  const download = async (iid, num) => {
    try {
      const resp = await api.get(`/learner/invoices/${iid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${num}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="My Invoices"><LoadingBlock /></DashboardLayout>;
  const hasNothing = rows.length === 0 && receipts.length === 0;
  return (
    <DashboardLayout title="My Invoices" breadcrumbs={["My Learning", "Invoices"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="learner-invoices">
        {hasNothing ? (
          <EmptyState title="No invoices" description="Your invoices will appear after a successful purchase." icon={FileText} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3 text-left">Invoice</th><th className="p-3 text-left">Date</th><th className="p-3 text-right">Amount</th><th className="p-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((i) => (
                <tr key={i.id} data-testid={`invoice-${i.id}`}>
                  <td className="p-3 font-mono text-xs">{i.invoice_number}</td>
                  <td className="p-3 text-[11px] text-slate-500">{i.invoice_date?.slice(0, 10)}</td>
                  <td className="p-3 text-right font-semibold">{INR(i.total_amount)}</td>
                  <td className="p-3 text-right">
                    <button type="button" onClick={() => download(i.id, i.invoice_number)}
                            data-testid={`invoice-download-${i.id}`}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
              {receipts.map((r) => (
                <tr key={r.id} data-testid={`enrollment-receipt-${r.id}`} className="bg-slate-50/40">
                  <td className="p-3">
                    <div className="font-medium text-slate-800">{r.course_title}</div>
                    <div className="text-[11px] text-slate-500">Enrolled by {r.institute_name}</div>
                  </td>
                  <td className="p-3 text-[11px] text-slate-500">{r.enrolled_on}</td>
                  <td className="p-3 text-right text-[11px] text-slate-500 italic">Paid by institute</td>
                  <td className="p-3 text-right">
                    <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 border border-emerald-200">
                      Enrolment receipt
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — REVENUE DASHBOARD
// ============================================================

export function AdminRevenueDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data: r } = await api.get("/admin/revenue/dashboard"); setData(r.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="Revenue Dashboard"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Revenue Dashboard" breadcrumbs={["Super Admin", "Revenue"]}>
      <div className="space-y-4" data-testid="admin-revenue-dashboard">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total revenue" value={INR(data.total_revenue)} icon="TrendingUp" tone="navy" testId="rev-kpi-total" index={0} />
          <StatCard label="This month" value={INR(data.revenue_this_month)} icon="Sparkles" tone="green" testId="rev-kpi-month" index={1} />
          <StatCard label="Paid orders" value={data.orders_paid} icon="CircleCheckBig" tone="green" testId="rev-kpi-paid" index={2} />
          <StatCard label="Pending" value={data.orders_pending} icon="Clock" tone="amber" testId="rev-kpi-pending" index={3} />
          <StatCard label="Failed" value={data.orders_failed} icon="AlertTriangle" tone="rose" testId="rev-kpi-failed" index={4} />
          <StatCard label="Refunded" value={INR(data.refunded_amount)} icon="RefreshCw" tone="slate" testId="rev-kpi-refunded" index={5} />
          <StatCard label="Active subs" value={data.active_subscriptions} icon="Users" tone="navy" testId="rev-kpi-subs" index={6} />
          <StatCard label="GST collected" value={INR(data.gst_collected)} icon="Receipt" tone="green" testId="rev-kpi-gst" index={7} />
        </section>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Top selling courses</div>
          {data.top_courses.length === 0 ? (
            <EmptyState title="No sales yet" description="Course revenue breakdown will appear here." icon={Package} />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-widest text-slate-400">
                <tr><th className="text-left py-2">Course</th><th className="text-right">Orders</th><th className="text-right">Revenue</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.top_courses.map((c, i) => (
                  <tr key={i}><td className="py-2">{c.course_title}</td><td className="text-right">{c.count}</td><td className="text-right font-semibold">{INR(c.revenue)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

// ============================================================
// SUPER ADMIN — GATEWAYS, COUPONS, PLANS, ORDERS, INVOICES, REFUNDS
// ============================================================

export function AdminGatewaysPage() {
  const [rows, setRows] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [editing, setEditing] = useState(null); // gateway obj or "new" or null
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const EMPTY_FORM = {
    gateway_name: "Razorpay", gateway_code: "razorpay",
    api_key_id: "", api_key_secret: "", webhook_secret: "",
    environment: "test", currency: "INR", is_default: true, status: "active",
  };

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/payment-gateways"); setRows(data.data?.gateways || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  const test = async (id) => {
    try { const { data } = await api.post(`/admin/payment-gateways/${id}/test`); setTestResult({ id, ...(data.data || {}) }); toast.success("Test complete"); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const openNew = () => { setForm({ ...EMPTY_FORM }); setEditing("new"); };
  const openEdit = (g) => {
    setForm({
      gateway_name: g.gateway_name, gateway_code: g.gateway_code,
      api_key_id: g.api_key_id || "", api_key_secret: "", webhook_secret: "",
      environment: g.environment, currency: g.currency,
      is_default: !!g.is_default, status: g.status,
    });
    setEditing(g);
  };
  const close = () => { setEditing(null); setForm({}); };
  const save = async () => {
    if (!form.gateway_name || !form.gateway_code) return toast.error("Name and code are required");
    setBusy(true);
    try {
      // Omit blank secrets so they don't overwrite existing on edit
      const payload = { ...form };
      if (editing !== "new") {
        if (!payload.api_key_secret) delete payload.api_key_secret;
        if (!payload.webhook_secret) delete payload.webhook_secret;
      }
      if (editing === "new") {
        await api.post("/admin/payment-gateways", payload);
        toast.success("Gateway added");
      } else {
        await api.put(`/admin/payment-gateways/${editing.id}`, payload);
        toast.success("Gateway updated");
      }
      close(); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (rows === null) return <DashboardLayout title="Payment Gateways"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Payment Gateways" breadcrumbs={["Super Admin", "Gateways"]}>
      <div className="space-y-3" data-testid="admin-gateways">
        <div className="flex items-center justify-between">
          <p className="text-xs italic text-slate-500">
            Env vars (<code>RAZORPAY_KEY_ID</code> / <code>RAZORPAY_KEY_SECRET</code>) take priority if set.
            Otherwise the default active gateway configured here is used. Secrets never leave the server after being saved.
          </p>
          <button onClick={openNew} data-testid="add-gateway-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-[#101876] px-4 py-2 text-sm font-bold text-white hover:bg-[#0d1360]">
            + New gateway
          </button>
        </div>

        {rows.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-xs text-slate-500">
            No payment gateway configured yet. Click <strong>+ New gateway</strong> to add Razorpay keys.
          </div>
        )}

        {rows.map((g) => (
          <div key={g.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`gateway-${g.gateway_code}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold">{g.gateway_name}
                  {g.is_default && <span className="ml-2 rounded-full bg-[#329112]/10 px-2 py-0.5 text-[10px] font-bold text-[#329112]">DEFAULT</span>}
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-slate-500">
                  {g.gateway_code} · {g.environment} · {g.currency}
                </div>
                {g.api_key_id && (
                  <div className="mt-1 font-mono text-[11px] text-slate-600">
                    Key ID: <span className="font-semibold">{g.api_key_id}</span>
                    {g.api_key_secret_masked && <> · Secret: <span className="font-semibold">{g.api_key_secret_masked}</span></>}
                  </div>
                )}
                <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  source: {g.source}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {g.test_mode_active && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800">TEST MODE</span>}
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${g.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{g.status}</span>
                <button type="button" onClick={() => openEdit(g)} data-testid={`gateway-edit-${g.gateway_code}`}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Edit</button>
                <button type="button" onClick={() => test(g.id)} data-testid={`gateway-test-${g.gateway_code}`}
                        className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">Test</button>
              </div>
            </div>
            {testResult?.id === g.id && (
              <div className="mt-2 rounded-lg bg-slate-50 p-2 text-xs">
                {testResult.message || `Configured: ${testResult.configured ? "yes" : "no"}${testResult.test_order_id ? ` · Test order: ${testResult.test_order_id}` : ""}`}
              </div>
            )}
          </div>
        ))}

        {editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="gateway-form-modal">
            <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-black text-slate-900">
                  {editing === "new" ? "Add payment gateway" : "Edit gateway"}
                </div>
                <button onClick={close} className="rounded-lg p-1 hover:bg-slate-100" data-testid="gateway-form-close">×</button>
              </div>
              <div className="grid gap-3">
                <label className="text-xs">
                  <span className="mb-1 block font-bold text-slate-700">Gateway name</span>
                  <input value={form.gateway_name} onChange={(e) => setForm({ ...form, gateway_name: e.target.value })}
                    data-testid="gateway-name-input"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold text-slate-700">Gateway code</span>
                  <select value={form.gateway_code} onChange={(e) => setForm({ ...form, gateway_code: e.target.value })}
                    disabled={editing !== "new"}
                    data-testid="gateway-code-input"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none disabled:bg-slate-50">
                    <option value="razorpay">razorpay</option>
                    <option value="phonepe">phonepe</option>
                    <option value="stripe">stripe</option>
                    <option value="manual_bank">manual_bank</option>
                    <option value="upi_qr">upi_qr</option>
                  </select>
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold text-slate-700">Key ID (public)</span>
                  <input value={form.api_key_id} onChange={(e) => setForm({ ...form, api_key_id: e.target.value })}
                    placeholder="rzp_test_XXXXXXXXXXXX"
                    data-testid="gateway-key-input"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-[#101876] focus:outline-none" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold text-slate-700">
                    Key Secret {editing !== "new" && <span className="font-normal text-slate-400">(leave blank to keep existing)</span>}
                  </span>
                  <input type="password" value={form.api_key_secret} onChange={(e) => setForm({ ...form, api_key_secret: e.target.value })}
                    placeholder={editing !== "new" ? "••••••••" : "Paste the secret from Razorpay dashboard"}
                    data-testid="gateway-secret-input"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-[#101876] focus:outline-none" />
                </label>
                <label className="text-xs">
                  <span className="mb-1 block font-bold text-slate-700">
                    Webhook Secret {editing !== "new" && <span className="font-normal text-slate-400">(optional, blank keeps existing)</span>}
                  </span>
                  <input type="password" value={form.webhook_secret} onChange={(e) => setForm({ ...form, webhook_secret: e.target.value })}
                    data-testid="gateway-webhook-input"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm focus:border-[#101876] focus:outline-none" />
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-xs">
                    <span className="mb-1 block font-bold text-slate-700">Environment</span>
                    <select value={form.environment} onChange={(e) => setForm({ ...form, environment: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none">
                      <option value="test">test</option>
                      <option value="production">production</option>
                    </select>
                  </label>
                  <label className="text-xs">
                    <span className="mb-1 block font-bold text-slate-700">Currency</span>
                    <input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none" />
                  </label>
                </div>
                <div className="flex flex-wrap gap-4 text-xs">
                  <label className="inline-flex items-center gap-2 font-semibold text-slate-700">
                    <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                      data-testid="gateway-default-input" /> Set as default
                  </label>
                  <label className="inline-flex items-center gap-2 font-semibold text-slate-700">
                    <input type="checkbox" checked={form.status === "active"}
                      onChange={(e) => setForm({ ...form, status: e.target.checked ? "active" : "inactive" })}
                      data-testid="gateway-active-input" /> Active
                  </label>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={close} className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
                <button onClick={save} disabled={busy} data-testid="gateway-save-btn"
                  className="rounded-lg bg-[#101876] px-5 py-2 text-xs font-bold text-white hover:bg-[#0d1360] disabled:opacity-60">
                  {busy ? "Saving…" : "Save gateway"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

const EMPTY_COUPON = {
  coupon_code: "", coupon_name: "", description: "",
  discount_type: "percentage", discount_value: 10,
  max_discount_amount: 0, minimum_order_amount: 0,
  applicable_courses_json: [], applicable_categories_json: [], applicable_organizations_json: [],
  applicable_user_type: "all",
  usage_limit_overall: 0, usage_limit_per_user: 0,
  valid_from: "", valid_till: "",
  status: "active",
  show_on_homepage: false, show_on_course_detail: false,
  show_on_cart: true, show_on_checkout: true,
  auto_apply_coupon: false, featured_coupon: false,
  coupon_badge_text: "", display_order: 0, terms_and_conditions: "",
};

export function AdminCouponsPage() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);  // coupon obj / "new" / null
  const [form, setForm] = useState(EMPTY_COUPON);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/coupons", { params: { status_filter: statusFilter } });
      setRows(data.data?.coupons || []);
    }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const lc = useLifecycleActions({
    basePath: "/admin/coupons",
    labelOf: (row) => `"${row.coupon_code}"`,
    onDone: load,
  });

  const openNew = () => { setForm(EMPTY_COUPON); setEditing("new"); };
  const openEdit = (c) => {
    setForm({ ...EMPTY_COUPON, ...c,
      valid_from: (c.valid_from || "").slice(0, 10),
      valid_till: (c.valid_till || "").slice(0, 10),
    });
    setEditing(c);
  };
  const close = () => { setEditing(null); setForm(EMPTY_COUPON); };

  const save = async () => {
    if (!form.coupon_code || !form.coupon_name) return toast.error("Code and name required");
    setBusy(true);
    try {
      const payload = { ...form,
        discount_value: +form.discount_value,
        max_discount_amount: +form.max_discount_amount,
        minimum_order_amount: +form.minimum_order_amount,
        usage_limit_overall: +form.usage_limit_overall,
        usage_limit_per_user: +form.usage_limit_per_user,
        display_order: +form.display_order,
        valid_from: form.valid_from ? new Date(form.valid_from).toISOString() : null,
        valid_till: form.valid_till ? new Date(form.valid_till + "T23:59:59").toISOString() : null,
      };
      if (editing === "new") {
        await api.post("/admin/coupons", payload);
        toast.success("Coupon created");
      } else {
        await api.put(`/admin/coupons/${editing.id}`, payload);
        toast.success("Coupon updated");
      }
      close(); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (c) => {
    // Legacy helper still used elsewhere; new flow goes through lc.open().
    try {
      await api.patch(`/admin/coupons/${c.id}/status`, { status: c.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  void toggleStatus;

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/admin/coupons/${c.id}/duplicate`);
      toast.success(`Duplicated as ${data.data.coupon_code} (inactive draft)`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (rows === null) return <DashboardLayout title="Coupons"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Coupons" breadcrumbs={["Super Admin", "Coupons"]}>
      <div className="mb-4 flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs font-semibold text-slate-500">{rows.length} coupons</div>
          <StatusFilterTabs value={statusFilter} onChange={setStatusFilter} testId="coupons-status-filter" />
        </div>
        <button onClick={openNew} data-testid="coupon-new"
          className="inline-flex items-center gap-2 rounded-xl bg-[#101876] px-4 py-2 text-sm font-bold text-white hover:bg-[#0d1360]">
          + New coupon
        </button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-coupons">
        {rows.map((c) => (
          <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`coupon-${c.coupon_code}`}>
            <div className="flex items-center justify-between">
              <div className="text-lg font-bold text-[#101876] font-mono">{c.coupon_code}</div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-500"}`}>{c.status}</span>
            </div>
            <div className="text-sm text-slate-700 mt-1">{c.coupon_name}</div>
            <div className="mt-2 text-xs text-slate-600">
              {c.discount_type === "percentage" ? `${c.discount_value}% off` : `${INR(c.discount_value)} off`}
              {c.max_discount_amount ? ` (max ${INR(c.max_discount_amount)})` : ""}
              {c.minimum_order_amount ? ` · Min order ${INR(c.minimum_order_amount)}` : ""}
            </div>
            <div className="mt-2 flex flex-wrap gap-1 text-[10px] font-semibold">
              {c.show_on_homepage && <span className="rounded-full bg-blue-50 text-blue-700 px-1.5 py-0.5">HOMEPAGE</span>}
              {c.show_on_course_detail && <span className="rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5">COURSE</span>}
              {c.show_on_cart && <span className="rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5">CART</span>}
              {c.show_on_checkout && <span className="rounded-full bg-slate-100 text-slate-700 px-1.5 py-0.5">CHECKOUT</span>}
              {c.auto_apply_coupon && <span className="rounded-full bg-emerald-50 text-emerald-700 px-1.5 py-0.5">AUTO</span>}
              {c.featured_coupon && <span className="rounded-full bg-amber-50 text-amber-700 px-1.5 py-0.5">FEATURED</span>}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1 justify-between">
              <div className="flex flex-wrap gap-1">
                <button onClick={() => openEdit(c)} data-testid={`coupon-edit-${c.coupon_code}`}
                  className="rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-semibold hover:bg-slate-50">Edit</button>
                <button onClick={() => duplicate(c)} data-testid={`coupon-duplicate-${c.coupon_code}`}
                  className="rounded-lg border border-slate-200 px-3 py-1 text-[11px] font-semibold hover:bg-slate-50">Duplicate</button>
              </div>
              <RowActionsMenu
                testId={`coupon-actions-${c.coupon_code}`}
                status={c.deleted_at ? "deleted" : c.status}
                onView={() => openEdit(c)}
                onEdit={() => openEdit(c)}
                onDeactivate={() => lc.open("deactivate", c)}
                onReactivate={() => lc.open("reactivate", c)}
                onDelete={() => lc.open("delete", c)}
                onRestore={() => lc.open("restore", c)}
              />
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" data-testid="coupon-form-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm font-black">{editing === "new" ? "New coupon" : `Edit ${editing.coupon_code}`}</div>
              <button onClick={close} className="rounded-lg p-1 hover:bg-slate-100">×</button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Coupon code">
                <input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })}
                  disabled={editing !== "new"} data-testid="coupon-code" className={couponInputCls + " font-mono"} />
              </Field>
              <Field label="Coupon name">
                <input value={form.coupon_name} onChange={(e) => setForm({ ...form, coupon_name: e.target.value })}
                  data-testid="coupon-name" className={couponInputCls} />
              </Field>
              <Field label="Discount type">
                <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                  data-testid="coupon-type" className={couponInputCls}>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed amount</option>
                </select>
              </Field>
              <Field label={form.discount_type === "percentage" ? "Discount %" : "Discount amount (₹)"}>
                <input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                  data-testid="coupon-value" className={couponInputCls} />
              </Field>
              <Field label="Max discount (₹, for % type)">
                <input type="number" value={form.max_discount_amount} onChange={(e) => setForm({ ...form, max_discount_amount: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Min order amount (₹)">
                <input type="number" value={form.minimum_order_amount} onChange={(e) => setForm({ ...form, minimum_order_amount: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Valid from">
                <input type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Valid till">
                <input type="date" value={form.valid_till} onChange={(e) => setForm({ ...form, valid_till: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Usage limit (overall, 0=unlimited)">
                <input type="number" value={form.usage_limit_overall} onChange={(e) => setForm({ ...form, usage_limit_overall: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Usage limit (per user)">
                <input type="number" value={form.usage_limit_per_user} onChange={(e) => setForm({ ...form, usage_limit_per_user: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Applicable user type">
                <select value={form.applicable_user_type} onChange={(e) => setForm({ ...form, applicable_user_type: e.target.value })}
                  className={couponInputCls}>
                  <option value="all">All users</option>
                  <option value="individual">Individual learners</option>
                  <option value="organization">Hospital / Institute</option>
                </select>
              </Field>
              <Field label="Badge text">
                <select value={form.coupon_badge_text} onChange={(e) => setForm({ ...form, coupon_badge_text: e.target.value })}
                  className={couponInputCls}>
                  <option value="">— none —</option>
                  <option value="Limited Time Offer">Limited Time Offer</option>
                  <option value="Best Offer">Best Offer</option>
                  <option value="Flat Discount">Flat Discount</option>
                  <option value="New User Offer">New User Offer</option>
                  <option value="Festival Offer">Festival Offer</option>
                </select>
              </Field>
              <Field label="Display order (lower = first)">
                <input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })}
                  className={couponInputCls} />
              </Field>
              <Field label="Status">
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className={couponInputCls}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                </select>
              </Field>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 rounded-xl border border-slate-100 bg-slate-50 p-3 text-xs">
              {[
                ["show_on_homepage", "Show on Homepage"],
                ["show_on_course_detail", "Show on Course Detail"],
                ["show_on_cart", "Show on Cart"],
                ["show_on_checkout", "Show on Checkout"],
                ["auto_apply_coupon", "Auto-apply at Checkout"],
                ["featured_coupon", "Featured coupon"],
              ].map(([k, l]) => (
                <label key={k} className="inline-flex items-center gap-2 font-semibold">
                  <input type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
                    data-testid={`coupon-${k}`} />
                  {l}
                </label>
              ))}
            </div>
            <Field label="Description">
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2} className={couponInputCls + " mt-3"} />
            </Field>
            <Field label="Terms & conditions (shown on cards)">
              <textarea value={form.terms_and_conditions} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })}
                rows={2} className={couponInputCls} />
            </Field>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={close} className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-semibold hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="coupon-save"
                className="rounded-lg bg-[#101876] px-5 py-2 text-xs font-bold text-white hover:bg-[#0d1360] disabled:opacity-60">
                {busy ? "Saving…" : "Save coupon"}
              </button>
            </div>
          </div>
        </div>
      )}
      {lc.dialog}
    </DashboardLayout>
  );
}

const couponInputCls = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-[#101876] focus:outline-none";
function Field({ label, children }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-bold text-slate-700">{label}</span>
      {children}
    </label>
  );
}

export function AdminSubscriptionPlansPage() {
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState(null); // null | {id?, plan_code, ...}
  const load = async () => {
    try { const { data } = await api.get("/admin/subscription-plans"); setRows(data.data?.plans || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const openNew = () => setDlg({
    plan_code: "", plan_name: "", plan_type: "yearly",
    price: 0, currency: "INR",
    gst_applicable: true, gst_percentage: 18,
    max_users: 100, ai_usage_limit: 0, support_level: "standard",
    validity_days: 365, status: "active",
  });
  const openEdit = (p) => setDlg({ ...p });
  const save = async () => {
    try {
      const payload = { ...dlg }; delete payload.created_at; delete payload.updated_at; delete payload.created_by;
      if (dlg.id) { await api.put(`/admin/subscription-plans/${dlg.id}`, payload); toast.success("Plan updated."); }
      else { await api.post("/admin/subscription-plans", payload); toast.success("Plan created."); }
      setDlg(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const setStatus = async (p, st) => {
    try { await api.patch(`/admin/subscription-plans/${p.id}/status`, { status: st }); toast.success(`Marked ${st}.`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Subscription Plans"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Subscription Plans" breadcrumbs={["Super Admin", "Plans"]}>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-slate-500">Create plans, edit pricing, and assign them to institutes.</div>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0c135c]" data-testid="plan-new-btn">+ New plan</button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-plans">
        {rows.map((p) => (
          <div key={p.id} className="rounded-2xl border-2 border-slate-200 bg-white p-5 hover:border-[#329112] transition-colors" data-testid={`plan-${p.plan_code}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#329112]">{p.plan_code}</div>
                <div className="mt-1 text-lg font-bold text-slate-800">{p.plan_name}</div>
              </div>
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${p.status==='active'?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-500'}`}>{p.status}</span>
            </div>
            <div className="mt-2 text-3xl font-black text-[#101876]">{INR(p.price)}<span className="text-xs font-normal text-slate-400">/{p.plan_type}</span></div>
            <ul className="mt-4 space-y-1 text-xs text-slate-600">
              <li>• {p.max_users} users</li>
              <li>• {p.ai_usage_limit ? `${p.ai_usage_limit.toLocaleString()} AI tokens` : "AI not included"}</li>
              <li>• {p.support_level} support</li>
              <li>• GST {p.gst_applicable ? `${p.gst_percentage || 18}%` : "not applicable"}</li>
              <li>• Validity {p.validity_days} days</li>
            </ul>
            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={() => openEdit(p)} className="text-xs font-bold text-[#101876] hover:underline" data-testid={`plan-edit-${p.plan_code}`}>Edit</button>
              <button onClick={() => setStatus(p, p.status === "active" ? "inactive" : "active")} className="text-xs font-bold text-slate-600 hover:underline" data-testid={`plan-toggle-${p.plan_code}`}>{p.status === "active" ? "Deactivate" : "Activate"}</button>
            </div>
          </div>
        ))}
      </div>
      {dlg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={(e) => e.target === e.currentTarget && setDlg(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" data-testid="plan-dialog">
            <h3 className="text-lg font-bold text-slate-900 mb-3">{dlg.id ? "Edit plan" : "New plan"}</h3>
            <div className="grid gap-3 sm:grid-cols-2 max-h-[70vh] overflow-y-auto pr-1">
              <label className="text-xs col-span-2"><span className="block font-bold mb-1">Plan code</span><input className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.plan_code} disabled={!!dlg.id} onChange={(e) => setDlg({ ...dlg, plan_code: e.target.value })} data-testid="plan-code" /></label>
              <label className="text-xs col-span-2"><span className="block font-bold mb-1">Plan name</span><input className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.plan_name} onChange={(e) => setDlg({ ...dlg, plan_name: e.target.value })} data-testid="plan-name" /></label>
              <label className="text-xs"><span className="block font-bold mb-1">Type</span><select className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.plan_type} onChange={(e) => setDlg({ ...dlg, plan_type: e.target.value })}><option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option></select></label>
              <label className="text-xs"><span className="block font-bold mb-1">Price ₹</span><input type="number" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.price} onChange={(e) => setDlg({ ...dlg, price: parseFloat(e.target.value || "0") })} data-testid="plan-price" /></label>
              <label className="text-xs"><span className="block font-bold mb-1">Max users</span><input type="number" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.max_users} onChange={(e) => setDlg({ ...dlg, max_users: parseInt(e.target.value || "0", 10) })} data-testid="plan-users" /></label>
              <label className="text-xs"><span className="block font-bold mb-1">AI tokens</span><input type="number" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.ai_usage_limit} onChange={(e) => setDlg({ ...dlg, ai_usage_limit: parseInt(e.target.value || "0", 10) })} data-testid="plan-ai" /></label>
              <label className="text-xs"><span className="block font-bold mb-1">Support</span><select className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.support_level} onChange={(e) => setDlg({ ...dlg, support_level: e.target.value })}><option value="basic">Basic</option><option value="standard">Standard</option><option value="premium">Premium</option><option value="dedicated">Dedicated</option></select></label>
              <label className="text-xs"><span className="block font-bold mb-1">Validity days</span><input type="number" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.validity_days} onChange={(e) => setDlg({ ...dlg, validity_days: parseInt(e.target.value || "0", 10) })} data-testid="plan-validity" /></label>
              <label className="text-xs flex items-center gap-2 mt-6"><input type="checkbox" checked={!!dlg.gst_applicable} onChange={(e) => setDlg({ ...dlg, gst_applicable: e.target.checked })} /> GST applicable</label>
              <label className="text-xs"><span className="block font-bold mb-1">GST %</span><input type="number" className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-sm" value={dlg.gst_percentage} disabled={!dlg.gst_applicable} onChange={(e) => setDlg({ ...dlg, gst_percentage: parseFloat(e.target.value || "0") })} data-testid="plan-gst" /></label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setDlg(null)} className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700" data-testid="plan-dlg-cancel">Cancel</button>
              <button onClick={save} className="rounded-md bg-[#101876] px-3 py-1.5 text-xs font-bold text-white" data-testid="plan-dlg-save">Save</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export function AdminOrdersPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/admin/revenue/orders"); setRows(data.data?.orders || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  if (rows === null) return <DashboardLayout title="Orders"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Order Management" breadcrumbs={["Super Admin", "Orders"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="admin-orders">
        {rows.length === 0 ? (
          <EmptyState title="No orders yet" description="Orders will appear here after learners purchase courses." icon={ShoppingCart} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3 text-left">Order</th><th className="p-3 text-left">Buyer</th><th className="p-3 text-right">Amount</th><th className="p-3 text-center">Status</th><th className="p-3 text-left">Date</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((o) => (
                <tr key={o.id} data-testid={`admin-order-${o.id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_number}</td>
                  <td className="p-3">{o.billing_name}<div className="text-[10px] text-slate-500">{o.billing_email}</div></td>
                  <td className="p-3 text-right font-semibold">{INR(o.total_amount)}</td>
                  <td className="p-3 text-center">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                      o.payment_status === "paid" ? "bg-green-100 text-green-800" :
                      o.payment_status === "failed" ? "bg-rose-100 text-rose-700" :
                      "bg-amber-100 text-amber-700"
                    }`}>{o.payment_status}</span>
                  </td>
                  <td className="p-3 text-[11px] text-slate-500">{new Date(o.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

export function AdminInvoicesPage() {
  const [rows, setRows] = useState(null);
  const [method, setMethod] = useState("all");
  const [action, setAction] = useState(null); // {kind:"cancel"|"delete", inv}
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const load = async () => {
    try { const { data } = await api.get("/admin/invoices"); setRows(data.data?.invoices || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  useEffect(() => { load(); }, []);
  const download = async (iid, num) => {
    try {
      const resp = await api.get(`/admin/invoices/${iid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${num}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const openAction = (kind, inv) => { setAction({ kind, inv }); setReason(""); setNotify(true); };
  const closeAction = () => { if (!submitting) { setAction(null); setReason(""); } };
  const confirmAction = async () => {
    if (!action) return;
    setSubmitting(true);
    try {
      const url = `/admin/invoices/${action.inv.id}/${action.kind}`;
      await api.post(url, { reason, notify_institute: notify });
      toast.success(action.kind === "cancel" ? "Invoice cancelled" : "Invoice deleted");
      setAction(null); setReason(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };
  const METHODS = useMemo(() => [
    { code: "all", label: "All" },
    { code: "razorpay", label: "Razorpay" },
    { code: "cash", label: "Cash" },
    { code: "upi", label: "UPI" },
    { code: "google_pay", label: "Google Pay" },
    { code: "phonepe", label: "PhonePe" },
    { code: "paytm", label: "Paytm" },
    { code: "bank_transfer", label: "Bank Transfer" },
    { code: "cheque", label: "Cheque" },
    { code: "card", label: "Card" },
    { code: "other", label: "Other" },
  ], []);
  const filtered = useMemo(() => {
    if (!rows) return [];
    if (method === "all") return rows;
    return rows.filter((i) => (i.payment_method || i.order_payment_method || "razorpay").toLowerCase() === method);
  }, [rows, method]);
  const countByMethod = useMemo(() => {
    const map = { all: (rows || []).length };
    (rows || []).forEach((i) => {
      const m = (i.payment_method || "razorpay").toLowerCase();
      map[m] = (map[m] || 0) + 1;
    });
    return map;
  }, [rows]);
  if (rows === null) return <DashboardLayout title="Invoices"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Invoice Register" breadcrumbs={["Super Admin", "Invoices"]}>
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="admin-invoice-filters">
        <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mr-1">Payment method:</span>
        {METHODS.map((m) => {
          const count = countByMethod[m.code] || 0;
          const active = method === m.code;
          return (
            <button
              key={m.code}
              onClick={() => setMethod(m.code)}
              data-testid={`admin-invoice-filter-${m.code}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${active ? "border-[#101876] bg-[#101876] text-white" : "border-slate-200 bg-white text-slate-700 hover:border-[#101876]/40"}`}
            >
              {m.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold ${active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"}`}>{count}</span>
            </button>
          );
        })}
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="admin-invoices">
        {filtered.length === 0 ? (
          <EmptyState title={method === "all" ? "No invoices" : `No ${METHODS.find((m) => m.code === method)?.label || ""} invoices`} description="Invoices generate automatically after successful payments." icon={FileText} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr><th className="p-3 text-left">Invoice</th><th className="p-3 text-left">Buyer</th><th className="p-3 text-left">Method</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">GST</th><th className="p-3 text-left">Status</th><th className="p-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((i) => {
                const status = (i.status || "active").toLowerCase();
                const isCancelled = status === "cancelled";
                const isDeleted = status === "deleted";
                return (
                  <tr key={i.id} data-testid={`admin-invoice-${i.id}`} className={isDeleted ? "bg-slate-50/60 text-slate-400" : ""}>
                    <td className="p-3 font-mono text-xs">{i.invoice_number}</td>
                    <td className="p-3">{isDeleted ? <span className="italic">— deleted —</span> : i.buyer_name}</td>
                    <td className="p-3 text-xs capitalize text-slate-600">{isDeleted ? "—" : (i.payment_method || "razorpay").replace(/_/g, " ")}</td>
                    <td className="p-3 text-right font-semibold">{isDeleted ? "—" : INR(i.total_amount)}</td>
                    <td className="p-3 text-right">{isDeleted ? "—" : INR(i.total_gst)}</td>
                    <td className="p-3">
                      {isDeleted ? (
                        <span className="inline-flex items-center rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-600">Deleted</span>
                      ) : isCancelled ? (
                        <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rose-700" data-testid={`admin-invoice-status-${i.id}`}>Cancelled</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-700">Paid</span>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        {!isDeleted && (
                          <button type="button" onClick={() => download(i.id, i.invoice_number)}
                                  data-testid={`admin-invoice-download-${i.id}`}
                                  className="inline-flex items-center gap-1 rounded-lg bg-[#101876] px-2.5 py-1.5 text-[11px] font-semibold text-white">
                            <Download className="h-3 w-3" /> PDF
                          </button>
                        )}
                        {!isCancelled && !isDeleted && (
                          <button type="button" onClick={() => openAction("cancel", i)}
                                  data-testid={`admin-invoice-cancel-${i.id}`}
                                  className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] font-semibold text-rose-700 hover:bg-rose-100">
                            <XCircle className="h-3 w-3" /> Cancel
                          </button>
                        )}
                        {!isDeleted && (
                          <button type="button" onClick={() => openAction("delete", i)}
                                  data-testid={`admin-invoice-delete-${i.id}`}
                                  className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50">
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <Dialog open={!!action} onOpenChange={(o) => { if (!o) closeAction(); }}>
        <DialogContent className="sm:max-w-md" data-testid="invoice-lifecycle-dialog">
          <DialogHeader>
            <DialogTitle>
              {action?.kind === "cancel" ? "Cancel invoice" : "Delete invoice"} {action?.inv?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p className="text-slate-600">
              {action?.kind === "cancel"
                ? "The invoice number stays in the sequence; the PDF will be stamped CANCELLED and any linked subscription will be voided."
                : "The invoice is replaced by a DELETED tombstone so the sequence has no gap. The PDF will no longer be downloadable and the order/subscription will be removed."}
            </p>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Reason (optional)</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                        data-testid="invoice-lifecycle-reason"
                        placeholder="e.g. Wrong buyer details, duplicate entry, etc."
                        className="mt-1 h-20 w-full rounded-lg border border-slate-200 p-2 text-sm outline-none focus:border-[#101876]" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                     data-testid="invoice-lifecycle-notify" />
              <span>Notify institute by email</span>
            </label>
          </div>
          <DialogFooter>
            <button type="button" onClick={closeAction} disabled={submitting}
                    data-testid="invoice-lifecycle-cancel"
                    className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Back
            </button>
            <button type="button" onClick={confirmAction} disabled={submitting}
                    data-testid="invoice-lifecycle-confirm"
                    className={`inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-bold text-white ${action?.kind === "cancel" ? "bg-rose-600 hover:bg-rose-700" : "bg-slate-800 hover:bg-slate-900"}`}>
              {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {action?.kind === "cancel" ? "Cancel invoice" : "Delete invoice"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export function AdminRefundsPage() {
  const [rows, setRows] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [processing, setProcessing] = useState(null); // rid currently being processed
  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get("/admin/refunds"); setRows(data.data?.refunds || []); }
    catch (err) { toast.error(formatApiError(err)); setRows([]); }
  };
  const act = async (id, action, extra) => {
    try {
      if (action === "process") setProcessing(id);
      const { data } = await api.post(`/admin/refunds/${id}/${action}`, extra || {});
      const gw = data?.data?.gateway_refund_id;
      toast.success(action === "process" && gw
        ? `Refund processed · ${gw}`
        : `Refund ${action}${action.endsWith("e") ? "d" : "ed"}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProcessing(null); }
  };
  const confirmReject = async () => {
    if (!rejectFor) return;
    await act(rejectFor.id, "reject", { remarks: rejectRemarks });
    setRejectFor(null); setRejectRemarks("");
  };
  if (rows === null) return <DashboardLayout title="Refunds"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Refund Management" breadcrumbs={["Super Admin", "Refunds"]}>
      <div className="mb-4 flex items-center justify-between">
        <div className="text-xs text-slate-500">{rows.length} request{rows.length === 1 ? "" : "s"}</div>
        <button type="button" onClick={() => setShowCreate(true)} data-testid="refund-new-btn"
                className="inline-flex items-center gap-2 rounded-lg bg-[#101876] px-4 py-2 text-xs font-bold text-white hover:bg-[#0a1155]">
          <RefreshCw className="h-3.5 w-3.5" /> New refund request
        </button>
      </div>
      <div className="space-y-2" data-testid="admin-refunds">
        {rows.length === 0 ? (
          <EmptyState title="No refund requests" description="Requests will appear here." icon={RefreshCw} />
        ) : rows.map((r) => (
          <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start justify-between gap-4" data-testid={`refund-${r.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold">
                {r.refund_request_number}
                {r.raised_by_learner && (
                  <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-[9px] font-bold uppercase text-indigo-700">by learner</span>
                )}
              </div>
              {r.learner?.name && (
                <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]" data-testid={`refund-learner-${r.id}`}>
                  <span className="font-bold text-slate-800">{r.learner.name}</span>
                  {r.learner.email && <span className="text-slate-500">{r.learner.email}</span>}
                  {r.learner.mobile && <span className="text-slate-500">· {r.learner.mobile}</span>}
                  {r.learner.employee_id && <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600">{r.learner.employee_id}</span>}
                  {r.learner.organization_name && <span className="text-[10px] text-slate-400">· {r.learner.organization_name}</span>}
                </div>
              )}
              {r.order?.order_number && (
                <div className="mt-0.5 text-[11px] text-slate-500" data-testid={`refund-order-${r.id}`}>
                  Order <span className="font-mono font-semibold text-slate-700">{r.order.order_number}</span>
                  {r.order.total_amount != null && <> · Paid {INR(r.order.total_amount)}</>}
                </div>
              )}
              <div className="mt-1 text-[11px] text-slate-500">Refund {INR(r.refund_amount)} · {r.refund_reason}</div>
              {r.remarks && <div className="text-[10px] text-rose-600 mt-1">Rejection note: {r.remarks}</div>}
              {r.gateway_refund_id && <div className="text-[10px] text-slate-400 mt-1 font-mono">{r.gateway_refund_id}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                r.request_status === "processed" ? "bg-green-100 text-green-800" :
                r.request_status === "approved" ? "bg-blue-100 text-blue-800" :
                r.request_status === "rejected" ? "bg-rose-100 text-rose-700" :
                "bg-amber-100 text-amber-800"
              }`}>{r.request_status}</span>
              {r.request_status === "pending" && (<>
                <button type="button" onClick={() => act(r.id, "approve")} data-testid={`refund-approve-${r.id}`} className="rounded-lg bg-green-600 px-3 py-1 text-xs font-semibold text-white hover:bg-green-700">Approve</button>
                <button type="button" onClick={() => { setRejectFor(r); setRejectRemarks(""); }} data-testid={`refund-reject-${r.id}`} className="rounded-lg bg-rose-600 px-3 py-1 text-xs font-semibold text-white hover:bg-rose-700">Reject</button>
              </>)}
              {r.request_status === "approved" && (
                <button type="button" disabled={processing === r.id} onClick={() => act(r.id, "process")} data-testid={`refund-process-${r.id}`} className="rounded-lg bg-[#101876] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0a1155] disabled:opacity-50">
                  {processing === r.id ? "Processing…" : "Process"}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {showCreate && (
        <CreateRefundDialog onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />
      )}
      {rejectFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="refund-reject-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="text-sm font-bold">Reject refund {rejectFor.refund_request_number}?</div>
            <textarea value={rejectRemarks} onChange={(e) => setRejectRemarks(e.target.value)}
                      placeholder="Add remarks (optional but recommended)…"
                      data-testid="refund-reject-remarks"
                      className="mt-3 h-24 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#101876]" />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setRejectFor(null)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold">Cancel</button>
              <button type="button" onClick={confirmReject} data-testid="refund-reject-confirm" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700">Confirm reject</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function CreateRefundDialog({ onClose, onDone }) {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/revenue/orders?status=paid");
        setOrders(data.data?.orders || []);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return orders.slice(0, 20);
    return orders.filter((o) =>
      (o.order_number || "").toLowerCase().includes(s) ||
      String(o.total_amount || "").includes(s),
    ).slice(0, 20);
  }, [orders, search]);
  const pick = (o) => { setSelected(o); setAmount(String(o.total_amount || "")); };
  const submit = async () => {
    if (!selected) { toast.error("Please pick an order."); return; }
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (!reason.trim()) { toast.error("Please share a reason."); return; }
    setSaving(true);
    try {
      await api.post("/admin/refunds", {
        order_id: selected.id, refund_amount: amt, refund_reason: reason.trim(),
      });
      toast.success("Refund request created.");
      onDone();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" data-testid="refund-create-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="text-sm font-bold text-slate-900">Create refund request</div>
        <div className="mt-3 space-y-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Order</label>
            {selected ? (
              <div className="mt-1 flex items-center justify-between rounded-lg border border-[#101876]/30 bg-[#101876]/5 px-3 py-2 text-xs">
                <div>
                  <div className="font-mono font-bold">{selected.order_number}</div>
                  <div className="text-slate-500">{INR(selected.total_amount)}</div>
                </div>
                <button type="button" onClick={() => setSelected(null)} className="text-[11px] font-bold text-rose-600 hover:underline">Change</button>
              </div>
            ) : (<>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search by order # or amount…"
                     data-testid="refund-order-search"
                     className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#101876]" />
              <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-100">
                {filtered.length === 0 && <div className="p-3 text-xs text-slate-400">No paid orders.</div>}
                {filtered.map((o) => (
                  <button key={o.id} type="button" onClick={() => pick(o)}
                          data-testid={`refund-order-pick-${o.id}`}
                          className="flex w-full items-center justify-between border-b border-slate-100 px-3 py-2 text-left text-xs hover:bg-slate-50">
                    <span className="font-mono font-bold">{o.order_number}</span>
                    <span className="text-slate-500">{INR(o.total_amount)}</span>
                  </button>
                ))}
              </div>
            </>)}
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Amount (₹)</label>
            <input value={amount} type="number" min="0" step="0.01" onChange={(e) => setAmount(e.target.value)}
                   data-testid="refund-amount-input"
                   className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#101876]" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Reason</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Why is this refund being issued?"
                      data-testid="refund-reason-input"
                      className="mt-1 h-24 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#101876]" />
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold">Cancel</button>
          <button type="button" onClick={submit} disabled={saving} data-testid="refund-create-submit"
                  className="rounded-lg bg-[#101876] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#0a1155] disabled:opacity-50">
            {saving ? "Creating…" : "Create request"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HOSPITAL ADMIN — BILLING DASHBOARD + SUBSCRIPTION + INVOICES
// ============================================================

export function OrgBillingDashboardPage() {
  const [data, setData] = useState(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewMsg, setRenewMsg] = useState("");
  const [renewing, setRenewing] = useState(false);
  const load = async () => {
    try { const { data: r } = await api.get("/org/billing/dashboard"); setData(r.data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);
  const submitRenewal = async () => {
    setRenewing(true);
    try {
      await api.post("/org/billing/request-renewal", { message: renewMsg });
      toast.success("Renewal requested. Steward will contact you shortly.");
      setRenewOpen(false); setRenewMsg("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRenewing(false); }
  };
  if (!data) return <DashboardLayout title="Billing"><LoadingBlock /></DashboardLayout>;
  const sub = data.subscription; const plan = data.plan; const seat = data.seat_usage || {};
  const eff = data.effective_status || "none";
  const days = data.days_remaining;
  const hasPending = !!data.pending_renewal_request;
  const showRenew = plan && (eff === "in_grace" || eff === "expired" || (eff === "active" && typeof days === "number" && days <= 30));
  return (
    <DashboardLayout title="Billing Dashboard" breadcrumbs={["Hospital Admin", "Billing"]}>
      <div className="space-y-4" data-testid="org-billing-dashboard">
        {(eff === "in_grace" || eff === "expired") && (
          <div className={`rounded-2xl border-2 p-4 flex items-start justify-between gap-4 flex-wrap ${eff === "expired" ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"}`} data-testid="ob-renewal-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`h-5 w-5 mt-0.5 ${eff === "expired" ? "text-rose-700" : "text-amber-700"}`} />
              <div>
                <div className={`text-sm font-bold ${eff === "expired" ? "text-rose-800" : "text-amber-800"}`}>
                  {eff === "expired" ? "Your subscription has expired" : "Subscription in 7-day grace period"}
                </div>
                <div className={`mt-0.5 text-xs ${eff === "expired" ? "text-rose-700" : "text-amber-700"}`}>
                  {eff === "expired"
                    ? "New enrolments and paid features are paused. Request a renewal to restore access."
                    : `Access continues for ${days} more day${days === 1 ? "" : "s"}. Request renewal to avoid auto-deactivation.`}
                </div>
              </div>
            </div>
            {hasPending ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-200" data-testid="ob-renewal-pending">
                Renewal request pending
              </span>
            ) : (
              <button onClick={() => setRenewOpen(true)} className="inline-flex items-center gap-1.5 rounded-md bg-[#329112] px-4 py-2 text-sm font-bold text-white hover:bg-[#256b0d]" data-testid="ob-request-renewal-btn">
                <RefreshCw className="h-4 w-4" /> Request Renewal
              </button>
            )}
          </div>
        )}
        <div className="rounded-2xl border-2 border-[#329112] bg-gradient-to-br from-white to-green-50 p-5" data-testid="org-current-plan">
          <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-[#101876]" /><div className="text-xs font-bold uppercase tracking-widest text-slate-500">Current plan</div></div>
          {plan ? (
            <>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <div className="text-2xl font-black text-[#101876]">{plan.plan_name}</div>
                {eff === "active" && typeof days === "number" && (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-emerald-800">Active · {days}d left</span>
                )}
                {eff === "in_grace" && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">In grace · {days}d left</span>
                )}
                {eff === "expired" && (
                  <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-rose-800">Expired</span>
                )}
              </div>
              <div className="text-xs text-slate-500">Valid till {sub?.end_date?.slice(0, 10)}</div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
                <div><strong>{seat.seats_used}</strong> / {seat.seats_purchased} seats used</div>
                <div><strong>{sub?.subscription_status}</strong> status</div>
                <div><strong>{plan.support_level}</strong> support</div>
                {showRenew && !hasPending && eff === "active" && (
                  <button onClick={() => setRenewOpen(true)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-[#329112] bg-white px-3 py-1 text-xs font-bold text-[#329112] hover:bg-[#329112]/5" data-testid="ob-request-renewal-btn-inline">
                    <RefreshCw className="h-3 w-3" /> Request Renewal
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="mt-2 text-sm text-slate-500">No active subscription. Contact Steward sales to enable a plan.</div>
          )}
        </div>
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {plan && (
            <div className={`rounded-2xl border-2 p-5 ${eff === "expired" ? "border-rose-300 bg-rose-50" : eff === "in_grace" ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50"}`} data-testid="ob-days-left-card">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Days remaining</div>
              <div className={`mt-1 text-4xl font-black ${eff === "expired" ? "text-rose-700" : eff === "in_grace" ? "text-amber-700" : "text-emerald-700"}`}>
                {eff === "expired" ? "0" : (typeof days === "number" ? Math.max(0, days) : "—")}
              </div>
              <div className="text-[11px] font-semibold text-slate-600 mt-0.5">
                {eff === "in_grace" ? `In 7-day grace · until ${(data.grace_ends_on || "").slice(0, 10)}` : `Ends ${sub?.end_date?.slice(0, 10)}`}
              </div>
            </div>
          )}
          <StatCard label="Seats used" value={seat.seats_used || 0} icon="Users" tone="navy" testId="ob-kpi-used" index={0} />
          <StatCard label="Available" value={seat.seats_available || 0} icon="Users" tone="green" testId="ob-kpi-available" index={1} />
          <StatCard label="Paid invoices" value={data.invoices_paid} icon="CircleCheckBig" tone="green" testId="ob-kpi-paid" index={2} />
          {!plan && <StatCard label="Pending" value={data.invoices_pending} icon="Clock" tone="amber" testId="ob-kpi-pending" index={3} />}
        </section>
        {seat.warning && (
          <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 text-sm text-amber-800" data-testid="ob-seat-warning">
            <AlertTriangle className="inline h-4 w-4 mr-1" /> Seat limit reached. Contact your admin to upgrade.
          </div>
        )}
      </div>

      <Dialog open={renewOpen} onOpenChange={setRenewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Request subscription renewal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-slate-600">Steward will receive this ping instantly and reach out to finalize your renewal. Add any preferences below (seat count changes, plan upgrade, etc.).</div>
            <textarea rows={4} value={renewMsg} onChange={(e) => setRenewMsg(e.target.value)} placeholder="Optional message…" className="w-full rounded-md border border-slate-200 p-2 text-sm" data-testid="ob-renewal-message" />
          </div>
          <DialogFooter>
            <button onClick={() => setRenewOpen(false)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submitRenewal} disabled={renewing} className="inline-flex items-center gap-2 rounded-md bg-[#329112] px-4 py-2 text-sm font-semibold text-white hover:bg-[#256b0d] disabled:opacity-70" data-testid="ob-renewal-submit">
              {renewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Send request
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export function OrgInvoicesPage() {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/org/billing/invoices"); setRows(data.data?.invoices || []); }
      catch (err) { toast.error(formatApiError(err)); setRows([]); }
    })();
  }, []);
  const download = async (iid, num) => {
    try {
      const resp = await api.get(`/org/billing/invoices/${iid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${num}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  if (rows === null) return <DashboardLayout title="Invoices"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="Organization Invoices" breadcrumbs={["Hospital Admin", "Invoices"]}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="org-invoices">
        {rows.length === 0 ? (
          <EmptyState title="No invoices" description="Your organization invoices will appear here." icon={FileText} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500"><tr><th className="p-3 text-left">Invoice</th><th className="p-3 text-left">Date</th><th className="p-3 text-right">Amount</th><th className="p-3 text-center">Status</th><th className="p-3"></th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((i) => (
                <tr key={i.id} data-testid={`org-inv-${i.id}`}>
                  <td className="p-3 font-mono text-xs">{i.invoice_number}</td>
                  <td className="p-3 text-[11px]">{i.invoice_date?.slice(0, 10)}</td>
                  <td className="p-3 text-right font-semibold">{INR(i.total_amount)}</td>
                  <td className="p-3 text-center"><span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-[10px] font-bold uppercase">{i.payment_status}</span></td>
                  <td className="p-3 text-right">
                    <button type="button" onClick={() => download(i.id, i.invoice_number)}
                            data-testid={`org-inv-download-${i.id}`}
                            className="inline-flex items-center gap-1 rounded-lg bg-[#101876] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0c135c]">
                      <Download className="h-3.5 w-3.5" /> PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardLayout>
  );
}

export function OrgAIUsagePage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data: r } = await api.get("/org/billing/ai-usage"); setData(r.data); }
      catch (err) { toast.error(formatApiError(err)); }
    })();
  }, []);
  if (!data) return <DashboardLayout title="AI Usage"><LoadingBlock /></DashboardLayout>;
  return (
    <DashboardLayout title="AI Usage Billing" breadcrumbs={["Hospital Admin", "AI Usage"]}>
      <div className="space-y-4" data-testid="org-ai-usage">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Requests (30d)" value={data.requests} icon="Sparkles" tone="navy" testId="au-kpi-req" index={0} />
          <StatCard label="Tokens (30d)" value={(data.tokens || 0).toLocaleString()} icon="Activity" tone="green" testId="au-kpi-tokens" index={1} />
          <StatCard label="Plan limit" value={data.included_limit ? data.included_limit.toLocaleString() : "—"} icon="ShieldCheck" tone="slate" testId="au-kpi-limit" index={2} />
          <StatCard label="Extra usage" value={(data.extra_usage || 0).toLocaleString()} icon="AlertTriangle" tone={data.warning ? "rose" : "slate"} testId="au-kpi-extra" index={3} />
        </section>
        {data.warning && (
          <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 text-sm text-amber-800">
            <AlertTriangle className="inline h-4 w-4 mr-1" /> AI usage is above 80% of your plan limit for this month.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
